import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DatabaseBackup, Loader2, Search, Download, CheckCircle2, AlertTriangle,
  Plane, FileText, RefreshCw, Users, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";

const STATUSES = [
  { v: "terminado", l: "Terminado (facturado)" },
  { v: "cerrado", l: "Cerrado" },
  { v: "abierto", l: "Abierto" },
  { v: "cotizaciones", l: "Cotizaciones" },
  { v: "archivado", l: "Archivado" },
  { v: "cancelado", l: "Cancelado" },
];

export default function MigracionPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("terminado");
  const [dateFrom, setDateFrom] = useState("2024-01-01");
  const [dateTo, setDateTo] = useState("2026-12-31");
  const [candidates, setCandidates] = useState(null);
  const [selected, setSelected] = useState({});
  const [withFiles, setWithFiles] = useState(true);
  const [jobId, setJobId] = useState(null);

  const previewMut = useMutation({
    mutationFn: async () => (await api.post("/migracion/preview", { status, date_from: dateFrom, date_to: dateTo, limit: 60 })).data,
    onSuccess: (d) => { setCandidates(d.items); setSelected({}); },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo leer de SOFI 1.0")),
  });

  const importMut = useMutation({
    mutationFn: async () => {
      const trips = (candidates || []).filter((c) => selected[c.external_trip_id])
        .map((c) => ({ external_trip_id: c.external_trip_id, sofi1_status: c.sofi1_status, lead_name: c.lead_name }));
      return (await api.post("/migracion/importar", { trips, with_files: withFiles })).data;
    },
    onSuccess: (d) => { setJobId(d.job_id); toast.success(`Importando ${d.total} viaje(s)…`); },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo iniciar la importación")),
  });

  const jobQ = useQuery({
    queryKey: ["migracion-job", jobId],
    queryFn: async () => (await api.get(`/migracion/jobs/${jobId}`)).data,
    enabled: !!jobId,
    refetchInterval: (q) => (q.state.data && ["done", "error"].includes(q.state.data.status) ? false : 1500),
  });

  const importadosQ = useQuery({
    queryKey: ["migracion-importados"],
    queryFn: async () => (await api.get("/migracion/importados")).data,
  });

  // Lista de jobs: para mostrar automáticamente el progreso del job activo (aunque
  // se haya lanzado fuera de esta sesión, p.ej. el import masivo en segundo plano).
  const jobsListQ = useQuery({
    queryKey: ["migracion-jobs-list"],
    queryFn: async () => (await api.get("/migracion/jobs")).data,
    refetchInterval: 4000,
  });

  React.useEffect(() => {
    if (jobId) return;
    const items = jobsListQ.data?.items || [];
    if (!items.length) return;
    // Prioriza un job en curso; si no hay, muestra el más reciente.
    const active = items.find((j) => j.status === "running" || j.status === "pending");
    setJobId((active || items[0]).id);
  }, [jobsListQ.data, jobId]);

  React.useEffect(() => {
    if (jobQ.data && jobQ.data.status === "done") {
      qc.invalidateQueries({ queryKey: ["migracion-importados"] });
      previewMut.reset();
    }
  }, [jobQ.data?.status]); // eslint-disable-line

  const selCount = Object.values(selected).filter(Boolean).length;
  const toggleAll = (val) => {
    const next = {};
    (candidates || []).forEach((c) => { if (!c.ya_importado) next[c.external_trip_id] = val; });
    setSelected(next);
  };

  const job = jobQ.data;

  return (
    <div className="space-y-5" data-testid="migracion-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <DatabaseBackup className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold tracking-tight">Migración SOFI 1.0</h1>
          <p className="text-sm text-muted-foreground">Importa viajes, reservas, estados, precios y facturas reales desde el sistema antiguo (solo lectura).</p>
        </div>
      </div>

      {/* Buscador de candidatos */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">1 · Buscar viajes en SOFI 1.0</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <div>
              <Label className="text-xs">Estado del viaje</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9" data-testid="mig-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Llegada desde</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="mig-from" /></div>
            <div><Label className="text-xs">Llegada hasta</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="mig-to" /></div>
            <div className="flex items-end">
              <Button className="w-full" onClick={() => previewMut.mutate()} disabled={previewMut.isPending} data-testid="mig-buscar">
                {previewMut.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Leyendo…</> : <><Search className="mr-1.5 h-4 w-4" />Buscar</>}
              </Button>
            </div>
          </div>

          {candidates && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{candidates.length} viajes</span>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => toggleAll(true)} data-testid="mig-select-all">Seleccionar todos</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => toggleAll(false)}>Ninguno</Button>
                </div>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs">
                  <Checkbox checked={withFiles} onCheckedChange={(v) => setWithFiles(!!v)} data-testid="mig-with-files" />
                  Importar facturas PDF (S3)
                </label>
              </div>

              <div className="max-h-[360px] overflow-y-auto rounded-lg border">
                {candidates.map((c) => (
                  <label key={c.external_trip_id} className={`flex items-center gap-3 border-b px-3 py-2 last:border-0 ${c.ya_importado ? "opacity-60" : "cursor-pointer hover:bg-muted/40"}`} data-testid={`mig-cand-${c.external_trip_id}`}>
                    <Checkbox checked={!!selected[c.external_trip_id]} disabled={c.ya_importado}
                      onCheckedChange={(v) => setSelected((p) => ({ ...p, [c.external_trip_id]: !!v }))}
                      data-testid={`mig-check-${c.external_trip_id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium">{c.lead_name}</span>
                        {c.ya_importado && <Badge className="bg-emerald-600 text-[10px] text-white">Ya importado</Badge>}
                      </div>
                      <p className="truncate text-xs text-muted-foreground">#{c.external_trip_id} · {c.arrival_date} · {c.estado} · {c.servicios} servicios · {c.agente_ventas || "—"}</p>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <span className="text-sm text-muted-foreground">{selCount} seleccionado(s)</span>
                <Button onClick={() => importMut.mutate()} disabled={selCount === 0 || importMut.isPending || (job && job.status === "running")} data-testid="mig-importar">
                  {importMut.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Iniciando…</> : <><Download className="mr-1.5 h-4 w-4" />Importar {selCount > 0 ? `(${selCount})` : ""}</>}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Progreso del job */}
      {job && (
        <Card data-testid="mig-job">
          <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base">
            {job.status === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : job.status === "error" ? <AlertTriangle className="h-4 w-4 text-rose-600" /> : <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            2 · Importación {job.status === "done" ? "completada" : job.status === "error" ? "con error" : "en curso"}
          </CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Progress value={job.total ? (job.done / job.total) * 100 : 0} />
            <p className="text-sm text-muted-foreground">{job.done} / {job.total} {job.current ? `· procesando: ${job.current}` : ""}</p>
            {job.error && <p className="rounded bg-rose-50 p-2 text-sm text-rose-700">{job.error}</p>}
            {(job.log || []).length > 0 && (
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border p-2 text-xs">
                {job.log.map((l, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {l.ok ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <AlertTriangle className="h-3 w-3 text-rose-600" />}
                    <span className="truncate">{l.lead}{l.ok ? ` — ${l.reservas} reservas, ${l.facturas} facturas, ${l.vouchers ?? 0} vouchers` : ` — ${l.error}`}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Ya migrados */}
      <Card>
        <CardHeader className="flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Viajes ya migrados</CardTitle>
          <Button size="sm" variant="outline" onClick={() => importadosQ.refetch()}><RefreshCw className="mr-1.5 h-4 w-4" />Actualizar</Button>
        </CardHeader>
        <CardContent>
          {importadosQ.isLoading ? <Skeleton className="h-24 w-full" /> : (importadosQ.data?.items || []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="mig-importados-empty">Aún no has migrado ningún viaje.</p>
          ) : (
            <div className="space-y-1.5" data-testid="mig-importados">
              {importadosQ.data.items.map((t) => (
                <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Plane className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm font-medium">{t.main_traveler}</span>
                    <Badge variant="outline" className="text-[10px]">{t.status}</Badge>
                    <span className="text-xs text-muted-foreground">#{t.external_trip_id} · {t.start_date || "—"}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{t.n_reservas} reservas</span>
                    <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{t.n_facturas} facturas</span>
                    <a href={`/app/trips?open=${t.id}`} className="flex items-center gap-1 text-primary hover:underline" data-testid={`mig-open-${t.id}`}><ExternalLink className="h-3 w-3" />Ver</a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
